'use client';
import useLocale from '@/hooks/useLocale';
import { getPlatform } from '@/utils/helpers';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { getLoveveryId } from '@/lib/loveveryIdManager';
import { useEffect, useState } from 'react';
import useSubscriptions, { UserSubscriptions } from './SubscriptionContext';
import useAuth, { AuthState } from './AuthContext';

export function buildLDContext({
  loveveryId,
  locale,
  platform,
  subscriptions,
  user,
}: {
  loveveryId: string | null;
  locale: string;
  platform: string;
  subscriptions?: UserSubscriptions;
  user: AuthState | null;
}) {
  if (!loveveryId) return;

  // Define the base user object for conditional addition
  const userContext: {
    key: string;
    kind: string;
    subscriptions?: {
      activeSubscriber?: boolean;
      isAgedOutSubscriber?: boolean;
    };
    email?: string;
  } = {
    key: loveveryId,
    kind: 'user',
  };

  // Conditionally add the subscriptions property when the user is logged in
  if (subscriptions) {
    userContext.subscriptions = {
      activeSubscriber: subscriptions.isActiveSubscriber,
      isAgedOutSubscriber: subscriptions.isAgedOutSubscriber,
    };
  }

  if (user) {
    userContext.email = user.attributes.email;
  }

  const context = {
    kind: 'multi',
    region: {
      key: process.env.NEXT_PUBLIC_REGION || '',
      kind: 'region',
    },
    locale: {
      key: locale,
      kind: 'locale',
    },
    user: userContext,
    platform: {
      key: platform,
      kind: 'platform',
    },
  };

  return context;
}

export const FeatureProvider = ({ children }: { children: any }) => {
  const [loveveryId, setLoveveryId] = useState<string | null>(null);
  const { data: subscriptions } = useSubscriptions();
  const { user } = useAuth();

  useEffect(() => {
    const fetchLoveveryId = async () => {
      const id = await getLoveveryId();
      setLoveveryId(id);
    };
    fetchLoveveryId();
  }, []);

  const locale = useLocale();
  const platform = getPlatform();
  const context = buildLDContext({
    loveveryId,
    locale,
    platform,
    subscriptions,
    user,
  });

  return (
    <LDProvider
      clientSideID={process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_KEY || ''}
      context={context}
      deferInitialization={true}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      {children}
    </LDProvider>
  );
};
